import { useState, useEffect, useCallback, useRef } from 'react'
import { toast } from 'react-toastify'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
    Box,
    Paper,
    Typography,
    Grid,
    Button,
    Divider,
    Chip,
    IconButton,
    Tooltip,
    Tab,
    Tabs,
    TextField,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Autocomplete,
    Popover,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    alpha,
} from '@mui/material'
import {
    ArrowBack,
    Email,
    Archive,
    Unarchive,
    Business,
    Send,
    History,
    Comment,
    Chat,
    Visibility,
    Person,
    Download,
    Close,
    PictureAsPdf,
    Assessment,
    Timer,
    Bolt,
    AttachFile,
    Shield,
    Star,
    CloudUpload,
    PlayArrow,
    VolumeUp,
    Label,
    Warning,
    SwapHoriz,
} from '@mui/icons-material'
import { useTranslation } from '@i18n'
import { useSubmission } from '@hooks/useSubmissions'
import { useDepartments, useDepartmentComments } from '@hooks/useDepartments'
import { useAuthStore } from '@store/authStore'
import { useUIStore } from '@store/uiStore'
import { formatDate, getBilingualValue } from '@utils/helpers'
import StatusChip from '@components/Common/StatusChip'
import Loading from '@components/Common/Loading'
import EmailModal from '@components/Dashboard/EmailModal'
import NavigateModal from '@components/Dashboard/NavigateModal'
import DepartmentApprovalPanel, { ApprovalTimeline } from '@components/Submissions/DepartmentApprovalPanel'
import { approvalApi } from '@api/endUser'
import { exportSubmissionPDF } from '@utils/pdfExport'
import { exportChatPDF } from '@utils/chatPdfExport'
import ConfirmDialog from '@components/Common/ConfirmDialog'
import axios from '@api/axios'

const SubmissionDetail = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const { t, language } = useTranslation()
    const { user } = useAuthStore()
    const { themeSettings } = useUIStore()

    const { submission, isLoading, isError } = useSubmission(id)
    const { comments } = useDepartmentComments(id)
    const { addComment, isCommenting } = useDepartments()

    const [activeTab, setActiveTab] = useState(0)
    const [emailModalOpen, setEmailModalOpen] = useState(false)
    const [navigateModalOpen, setNavigateModalOpen] = useState(false)
    const [newComment, setNewComment] = useState('')
    const [viewEmailData, setViewEmailData] = useState(null)
    
    // Staff Notes state
    const [newStaffNote, setNewStaffNote] = useState('')
    const [isAddingStaffNote, setIsAddingStaffNote] = useState(false)
    const [isDeletingStaffNote, setIsDeletingStaffNote] = useState(null)
    const [filePreview, setFilePreview] = useState(null) // { url, filename, isImage }
    const [imageError, setImageError] = useState(false) // Track image load errors
    const [downloadingFile, setDownloadingFile] = useState(false) // Track download state
    const [pdfExportDialogOpen, setPdfExportDialogOpen] = useState(false) // PDF language selection
    const [confirmDeleteNote, setConfirmDeleteNote] = useState({ open: false, noteId: null })

    // Category state
    const [assignedCategories, setAssignedCategories] = useState([])
    const [availableCategories, setAvailableCategories] = useState([])
    const [categoryLoading, setCategoryLoading] = useState(false)
    const [showCategoryEdit, setShowCategoryEdit] = useState(false)
    const [categoryCustomValues, setCategoryCustomValues] = useState({}) // { categoryId: 'custom text' }
    const [categoryAnchorEl, setCategoryAnchorEl] = useState(null)

    // Status change state
    const [statusHistoryLoading, setStatusHistoryLoading] = useState(false)
    const [statusChanging, setStatusChanging] = useState(false)
    const [statusHistory, setStatusHistory] = useState([])
    const queryClient = useQueryClient()


    const [uploadModalOpen, setUploadModalOpen] = useState(false)
    const [uploadFile, setUploadFile] = useState(null)
    const [isUploading, setIsUploading] = useState(false)

    // Leave confirmation state for incomplete submissions
    const [leaveDialogOpen, setLeaveDialogOpen] = useState(false)
    const pendingNavigation = useRef(null)

    // Determine if submission needs attention (non-completed status)
    const isIncomplete = submission && ['new', 'read'].includes(submission.status)
    const isStaffUser = user?.user_type === 'admin' || user?.user_type === 'form_manager'
    const shouldBlock = isIncomplete && isStaffUser

    // Intercept browser back button via popstate
    useEffect(() => {
        if (!shouldBlock) return
        const handlePopState = (e) => {
            // Push state back to prevent actual navigation
            window.history.pushState(null, '', window.location.href)
            pendingNavigation.current = '__BACK__'
            setLeaveDialogOpen(true)
        }
        // Push current state so we can intercept back
        window.history.pushState(null, '', window.location.href)
        window.addEventListener('popstate', handlePopState)
        return () => window.removeEventListener('popstate', handlePopState)
    }, [shouldBlock])

    // Browser beforeunload event (tab close / refresh)
    useEffect(() => {
        if (!shouldBlock) return
        const handler = (e) => {
            e.preventDefault()
            e.returnValue = ''
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [shouldBlock])

    // Guarded navigate — shows dialog if submission is incomplete
    const guardedNavigate = useCallback((path) => {
        if (shouldBlock) {
            pendingNavigation.current = path
            setLeaveDialogOpen(true)
        } else {
            navigate(path)
        }
    }, [shouldBlock, navigate])

    const handleLeaveConfirm = () => {
        setLeaveDialogOpen(false)
        const target = pendingNavigation.current
        pendingNavigation.current = null
        if (target === '__BACK__') {
            window.history.go(-2) // go back past the pushState we added
        } else if (target) {
            navigate(target)
        }
    }

    const handleLeaveCancel = () => {
        setLeaveDialogOpen(false)
        pendingNavigation.current = null
    }

    // Download file with correct filename (handles cross-origin)
    const downloadFile = async (url, filename) => {
        setDownloadingFile(true);
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up blob URL after download
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
        } catch (error) {
            console.error('Download error:', error);
            // Fallback: open in new tab if blob download fails
            window.open(url, '_blank');
        } finally {
            setDownloadingFile(false);
        }
    };

    const isDashboardUser = ['admin', 'dashboard'].includes(user?.user_type)
    const isDepartmentUser = user?.user_type === 'department'
    const isFormManager = user?.user_type === 'form_manager'
    const isAdmin = user?.user_type === 'admin'
    const isRTL = language === 'ar'

    // Check if this is an end-user submission (vs navigated submission)
    const isEndUserSubmission = !!submission?.end_user_id

    // Fetch approval records for this submission (for admin, form_manager, and department users)
    const { data: approvalData, refetch: refetchApproval } = useQuery({
        queryKey: ['submissionApproval', id, user?.department_id],
        queryFn: () => approvalApi.getBySubmission(id),
        enabled: (isDepartmentUser || isAdmin || isFormManager) && !!id,
    })

    // Get my department's pending approval (if any)
    const myApproval = approvalData?.data?.find(
        a => a.department_id === user?.department_id && a.status === 'pending'
    )
    const allApprovals = approvalData?.data || []

    // Fetch categories when submission loads
    useEffect(() => {
        if (!submission?.id || !submission?.form_id) return
        // Fetch assigned categories
        axios.get(`/submissions/${submission.id}/categories`)
            .then(res => {
                const cats = res?.data || []
                setAssignedCategories(cats)
                // Pre-populate custom values from existing assignments
                const cv = {}
                cats.forEach(c => { if (c.custom_value) cv[c.id] = c.custom_value })
                setCategoryCustomValues(cv)
            })
            .catch((err) => { console.error('[Categories] Failed to fetch assigned:', err) })
        // Fetch available categories for this form
        axios.get(`/categories?form_id=${submission.form_id}`)
            .then(res => setAvailableCategories((res?.data || []).filter(c => c.is_active)))
            .catch((err) => { console.error('[Categories] Failed to fetch available:', err) })
    }, [submission?.id, submission?.form_id])

    // Fetch status history when submission loads
    useEffect(() => {
        if (!submission?.id) return
        setStatusHistoryLoading(true)
        axios.get(`/submissions/${submission.id}/history`)
            .then(res => {
                const data = res?.data || res || []
                setStatusHistory(Array.isArray(data) ? data : [])
            })
            .catch(err => console.error('Failed to fetch status history:', err))
            .finally(() => setStatusHistoryLoading(false))
    }, [submission?.id])

    // Handle manual status change
    const handleStatusChange = async (newStatus) => {
        if (!submission || newStatus === submission.status) return
        setStatusChanging(true)
        try {
            await axios.put(`/submissions/${submission.id}/status`, { status: newStatus })
            toast.success(language === 'ar' ? 'تم تحديث الحالة بنجاح' : 'Status updated successfully')
            // Refetch submission data
            queryClient.invalidateQueries(['submission', id])
            // Refetch history
            const histRes = await axios.get(`/submissions/${submission.id}/history`)
            const data = histRes?.data || histRes || []
            setStatusHistory(Array.isArray(data) ? data : [])
        } catch (err) {
            console.error('Failed to update status:', err)
            toast.error(language === 'ar' ? 'فشل تحديث الحالة' : 'Failed to update status')
        } finally {
            setStatusChanging(false)
        }
    }

    const allowedStatuses = ['new', 'read', 'replied', 'pending_department', 'department_replied', 'closed']

    const handleUploadDocument = async () => {
        if (!uploadFile) return
        setIsUploading(true)
        try {
            const formData = new FormData()
            formData.append('document', uploadFile)

            await axios.post(`/submissions/${submission.id}/documents`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
            
            toast.success(isRTL ? 'تم رفع المستند بنجاح' : 'Document uploaded successfully')
            setUploadModalOpen(false)
            setUploadFile(null)
            
            // Refetch submission
            queryClient.invalidateQueries(['submission', id])
            
            // Refetch history
            const histRes = await axios.get(`/submissions/${submission.id}/history`)
            const data = histRes?.data || histRes || []
            setStatusHistory(Array.isArray(data) ? data : [])
        } catch (err) {
            console.error('Failed to upload document:', err)
            toast.error(err.response?.data?.message || (isRTL ? 'فشل إرفاق المستند' : 'Failed to upload document'))
        } finally {
            setIsUploading(false)
        }
    }

    // Save category assignments
    const handleSaveCategories = async (selectedCategories) => {
        setCategoryLoading(true)
        try {
            const res = await axios.post(`/submissions/${submission.id}/categories`, {
                category_ids: selectedCategories.map(c => c.id),
                custom_values: categoryCustomValues
            })
            setAssignedCategories(res?.data || selectedCategories)
            setShowCategoryEdit(false)
        } catch (err) {
            console.error('Failed to save categories:', err)
        } finally {
            setCategoryLoading(false)
        }
    }

    // Check if a category is "Other" type
    const isOtherCategory = (cat) => {
        const nameEn = (cat.name_en || '').toLowerCase().trim()
        const nameAr = (cat.name_ar || '').trim()
        return nameEn === 'other' || nameAr === 'أخرى'
    }

    // Handle tab query parameter for quick actions
    useEffect(() => {
        // Wait for submission to be loaded before handling tab params
        if (!submission) return

        const tab = searchParams.get('tab')
        if (!tab) return

        if (tab === 'email' && isDashboardUser) {
            setEmailModalOpen(true)
            setSearchParams({}, { replace: true }) // Clear the query param
        } else if (tab === 'navigate' && isDashboardUser) {
            setNavigateModalOpen(true)
            setSearchParams({}, { replace: true }) // Clear the query param
        } else if (tab === 'history') {
            setActiveTab(2) // History tab
            setSearchParams({}, { replace: true }) // Clear the query param
        } else if (tab === 'comments') {
            setActiveTab(1) // Comments tab
            setSearchParams({}, { replace: true }) // Clear the query param
        }
    }, [searchParams, isDashboardUser, setSearchParams, submission])

    // Staff Notes Handlers
    const handleAddStaffNote = async () => {
        if (!newStaffNote.trim()) return
        setIsAddingStaffNote(true)
        try {
            await axios.post(`/submissions/${submission.id}/notes`, { note: newStaffNote.trim() })
            toast.success(isRTL ? 'تمت إضافة الملاحظة بنجاح' : 'Note added successfully')
            setNewStaffNote('')
            queryClient.invalidateQueries(['submission', id])
        } catch (err) {
            console.error('Failed to add note:', err)
            toast.error(isRTL ? 'فشل إضافة الملاحظة' : 'Failed to add note')
        } finally {
            setIsAddingStaffNote(false)
        }
    }

    const handleDeleteStaffNote = async (noteId) => {
        setIsDeletingStaffNote(noteId)
        try {
            await axios.delete(`/submissions/${submission.id}/notes/${noteId}`)
            toast.success(isRTL ? 'تم حذف الملاحظة' : 'Note deleted')
            queryClient.invalidateQueries(['submission', id])
        } catch (err) {
            console.error('Failed to delete note:', err)
            toast.error(isRTL ? 'فشل الحذف' : 'Failed to delete note')
        } finally {
            setIsDeletingStaffNote(null)
        }
        setConfirmDeleteNote({ open: false, noteId: null })
    }

    const handleRequestDeleteNote = (noteId) => {
        setConfirmDeleteNote({ open: true, noteId })
    }

    const handleAddComment = () => {
        if (!newComment.trim()) return

        addComment({
            submissionId: parseInt(id),
            comment: newComment.trim(),
        })
        setNewComment('')
    }

    if (isLoading) {
        return (
            <Box sx={{ p: 4 }}>
                <Loading message={t('common.loading')} />
            </Box>
        )
    }

    if (isError || !submission) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">Submission not found</Alert>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate('/submissions')}
                    sx={{ mt: 2 }}
                >
                    Back to Submissions
                </Button>
            </Box>
        )
    }

    const getFieldLabel = (fieldId, parentFieldId = null) => {
        // Parse form_fields if it's a string
        let fields = submission.form_fields
        if (typeof fields === 'string') {
            try {
                fields = JSON.parse(fields)
            } catch (e) {
                fields = []
            }
        }

        if (!Array.isArray(fields)) return formatFieldId(fieldId);

        // First, try to find a top-level field match
        const topField = fields.find((f) => f.id === fieldId);
        if (topField) {
            return language === 'ar' && topField.label_ar
                ? topField.label_ar
                : (topField.label_en || topField.label || formatFieldId(fieldId));
        }

        // If we have a parent field context (for repeater subfields), search there
        if (parentFieldId) {
            const parentField = fields.find((f) => f.id === parentFieldId);
            if (parentField && Array.isArray(parentField.fields)) {
                const subField = parentField.fields.find((sf) => sf.id === fieldId);
                if (subField) {
                    return language === 'ar' && subField.label_ar
                        ? subField.label_ar
                        : (subField.label_en || subField.label || formatFieldId(fieldId));
                }
            }
        }

        // Search all repeater fields for a subfield match
        for (const field of fields) {
            if (field.type === 'repeater' && Array.isArray(field.fields)) {
                const subField = field.fields.find((sf) => sf.id === fieldId);
                if (subField) {
                    return language === 'ar' && subField.label_ar
                        ? subField.label_ar
                        : (subField.label_en || subField.label || formatFieldId(fieldId));
                }
            }
        }

        // Fallback: format the field ID nicely
        return formatFieldId(fieldId);
    }

    // Helper to format field IDs into readable labels
    const formatFieldId = (fieldId) => {
        if (!fieldId) return '';
        // Convert snake_case or camelCase to Title Case
        return fieldId
            .replace(/_/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/\b\w/g, c => c.toUpperCase());
    }

    return (
        <Box>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 3,
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => guardedNavigate('/submissions')}>
                        <ArrowBack />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {submission.submission_id}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <StatusChip status={submission.status} />
                            {/* Status Change Dropdown - for dashboard/admin/form_manager */}
                            {(isDashboardUser || isAdmin || isFormManager) && (
                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <Select
                                        value={submission.status || ''}
                                        onChange={(e) => handleStatusChange(e.target.value)}
                                        disabled={statusChanging}
                                        variant="outlined"
                                        size="small"
                                        startAdornment={statusChanging ? <CircularProgress size={16} sx={{ marginInlineEnd: 1 }} /> : <SwapHoriz fontSize="small" sx={{ marginInlineEnd: 0.5, color: 'text.secondary' }} />}
                                        sx={{
                                            fontSize: '0.8rem',
                                            height: 32,
                                            '& .MuiSelect-select': { py: 0.5, paddingInlineStart: 0 },
                                            '& .MuiSelect-icon': { right: isRTL ? 'unset' : 7, left: isRTL ? 7 : 'unset' },
                                        }}
                                    >
                                        {allowedStatuses.map(s => (
                                            <MenuItem key={s} value={s} sx={{ fontSize: '0.85rem' }}>
                                                {t(`status.${s}`)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            <Typography variant="body2" color="text.secondary">
                                {formatDate(submission.created_at, 'PPP p', language)}
                            </Typography>
                        </Box>
                        {/* Category Chips */}
                        {assignedCategories.length > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                <Label sx={{ fontSize: 16, color: '#1a3a6b', mr: 0.5 }} />
                                {assignedCategories.map(cat => {
                                    const catLabel = isRTL ? cat.name_ar : cat.name_en
                                    const displayLabel = isOtherCategory(cat) && cat.custom_value
                                        ? `${catLabel}: ${cat.custom_value}`
                                        : catLabel
                                    return (
                                        <Chip
                                            key={cat.id}
                                            label={displayLabel}
                                            size="small"
                                            sx={{
                                                bgcolor: cat.color + '20',
                                                color: cat.color,
                                                fontWeight: 600,
                                                border: `1px solid ${cat.color}40`,
                                                fontSize: '0.75rem',
                                            }}
                                        />
                                    )
                                })}
                            </Box>
                        )}
                    </Box>
                </Box>

                {(isDashboardUser || isFormManager) && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {availableCategories.length > 0 && (
                            <Button
                                variant="outlined"
                                startIcon={<Label />}
                                onClick={(e) => setCategoryAnchorEl(e.currentTarget)}
                                sx={{
                                    borderColor: assignedCategories.length > 0 ? '#1a3a6b' : undefined,
                                    color: assignedCategories.length > 0 ? '#1a3a6b' : undefined,
                                }}
                            >
                                {isRTL ? 'التصنيفات' : 'Categories'}
                                {assignedCategories.length > 0 && (
                                    <Chip
                                        label={assignedCategories.length}
                                        size="small"
                                        sx={{ ml: 0.5, height: 20, minWidth: 20, bgcolor: '#1a3a6b', color: '#fff', fontSize: '0.7rem' }}
                                    />
                                )}
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<CloudUpload />}
                            onClick={() => setUploadModalOpen(true)}
                        >
                            {isRTL ? 'إرفاق مستند' : 'Upload Doc'}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<PictureAsPdf />}
                            onClick={() => setPdfExportDialogOpen(true)}
                        >
                            {language === 'ar' ? 'تصدير PDF' : 'Export PDF'}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<Business />}
                            onClick={() => setNavigateModalOpen(true)}
                        >
                            {t('submissions.navigate')}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Email />}
                            onClick={() => setEmailModalOpen(true)}
                        >
                            {t('submissions.sendEmail')}
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Category Popover */}
            <Popover
                open={Boolean(categoryAnchorEl)}
                anchorEl={categoryAnchorEl}
                onClose={() => setCategoryAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { p: 2, borderRadius: 2, minWidth: 320, maxWidth: 400 } }}
            >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {isRTL ? 'تعيين التصنيفات' : 'Assign Categories'}
                </Typography>
                <Autocomplete
                    multiple
                    size="small"
                    options={availableCategories}
                    getOptionLabel={(opt) => isRTL ? opt.name_ar : opt.name_en}
                    value={assignedCategories.filter(ac => availableCategories.some(av => av.id === ac.id))}
                    onChange={(_, newVal) => handleSaveCategories(newVal)}
                    loading={categoryLoading}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    renderTags={(tagValue, getTagProps) =>
                        tagValue.map((opt, index) => (
                            <Chip
                                {...getTagProps({ index })}
                                key={opt.id}
                                label={isRTL ? opt.name_ar : opt.name_en}
                                size="small"
                                sx={{
                                    bgcolor: opt.color + '20',
                                    color: opt.color,
                                    fontWeight: 600,
                                    border: `1px solid ${opt.color}40`,
                                }}
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={isRTL ? 'اختر التصنيفات...' : 'Select categories...'}
                            variant="outlined"
                        />
                    )}
                    sx={{ minWidth: 280 }}
                />
                {/* Custom input for "Other" category */}
                {assignedCategories.filter(ac => isOtherCategory(ac)).length > 0 && (
                    <TextField
                        size="small"
                        fullWidth
                        label={isRTL ? 'تفاصيل "أخرى"' : '"Other" details'}
                        placeholder={isRTL ? 'اكتب التفاصيل هنا...' : 'Type details here...'}
                        value={categoryCustomValues[assignedCategories.find(ac => isOtherCategory(ac))?.id] || ''}
                        onChange={(e) => {
                            const otherCat = assignedCategories.find(ac => isOtherCategory(ac))
                            if (otherCat) {
                                setCategoryCustomValues(prev => ({ ...prev, [otherCat.id]: e.target.value }))
                            }
                        }}
                        onBlur={() => {
                            // Auto-save when user leaves the input
                            const currentSelected = assignedCategories.filter(ac => availableCategories.some(av => av.id === ac.id))
                            if (currentSelected.length > 0) {
                                handleSaveCategories(currentSelected)
                            }
                        }}
                        sx={{ mt: 1.5 }}
                        InputProps={{
                            sx: { fontSize: '0.875rem' }
                        }}
                    />
                )}
            </Popover>

            {/* Tabs - hide comments for end-user submissions */}
            <Paper sx={{ mb: 3, borderRadius: 2 }}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ px: 2 }}>
                    <Tab label={t('submission.formData')} />
                    {!isEndUserSubmission && <Tab label={t('submission.comments')} />}
                    <Tab label={t('submission.history')} />
                    <Tab label={t('submission.metadata')} />
                </Tabs>
            </Paper>

            {/* Tab Panels */}
            {activeTab === 0 && (
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            {submission.data?.chat_transcript ? (isRTL ? 'محادثة مباشرة' : 'Live Chat Conversation') : t('submission.formData')}
                        </Typography>
                        {submission.data?.chat_transcript && (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => exportChatPDF(submission, 'ar', themeSettings)}
                                    startIcon={<PictureAsPdf />}
                                >
                                    🇸🇦 PDF
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => exportChatPDF(submission, 'en', themeSettings)}
                                    startIcon={<PictureAsPdf />}
                                >
                                    🇬🇧 PDF
                                </Button>
                            </Box>
                        )}
                    </Box>

                    {/* Chat Submission View */}
                    {submission.data?.chat_transcript ? (() => {
                        const d = submission.data
                        return (
                            <Box>
                                {/* Chat Stats Summary */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    {[
                                        { label: isRTL ? 'الزائر' : 'Visitor', value: d.chat_visitor_name || '-', icon: <Person sx={{ fontSize: 18, color: '#1a3a6b' }} /> },
                                        { label: isRTL ? 'البريد' : 'Email', value: d.chat_visitor_email || '-', icon: <Email sx={{ fontSize: 18, color: '#1a3a6b' }} /> },
                                        { label: isRTL ? 'الحالة' : 'Status', value: d.chat_status || '-', icon: <Assessment sx={{ fontSize: 18, color: '#1a3a6b' }} /> },
                                        { label: isRTL ? 'المدة' : 'Duration', value: d.chat_duration_formatted || '-', icon: <Timer sx={{ fontSize: 18, color: '#1a3a6b' }} /> },
                                        { label: isRTL ? 'أول رد' : '1st Response', value: d.chat_first_response_formatted || '-', icon: <Bolt sx={{ fontSize: 18, color: '#1a3a6b' }} /> },
                                        { label: isRTL ? 'الرسائل' : 'Messages', value: `${d.chat_total_messages || 0} (${isRTL ? 'زائر' : 'V'}: ${d.chat_visitor_messages || 0}, ${isRTL ? 'وكيل' : 'A'}: ${d.chat_agent_messages || 0})`, icon: <Chat sx={{ fontSize: 18, color: '#1a3a6b' }} /> },
                                        { label: isRTL ? 'المرفقات' : 'Attachments', value: d.chat_attachment_count || 0, icon: <AttachFile sx={{ fontSize: 18, color: '#1a3a6b' }} /> },
                                        { label: isRTL ? 'الوكلاء' : 'Agents', value: d.chat_agents || '-', icon: <Shield sx={{ fontSize: 18, color: '#1a3a6b' }} /> },
                                    ].map((stat, idx) => (
                                        <Grid item xs={6} md={3} key={idx}>
                                            <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 2, textAlign: 'center' }}>
                                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                                                    {stat.icon} {stat.label}
                                                </Typography>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {stat.value}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>

                                {/* Timestamps */}
                                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                    {d.chat_created_at && (
                                        <Chip size="small" label={`${isRTL ? 'بدأت' : 'Started'}: ${new Date(d.chat_created_at).toLocaleString()}`} variant="outlined" />
                                    )}
                                    {d.chat_closed_at && (
                                        <Chip size="small" label={`${isRTL ? 'أُغلقت' : 'Closed'}: ${new Date(d.chat_closed_at).toLocaleString()}`} variant="outlined" color="error" />
                                    )}
                                    {d.chat_closed_by && (
                                        <Chip size="small" label={`${isRTL ? 'أغلقها' : 'Closed by'}: ${d.chat_closed_by}`} variant="outlined" color="warning" />
                                    )}
                                </Box>

                                {/* Agent Notes */}
                                {d.chat_agent_notes && (
                                    <Alert severity="info" sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" fontWeight={700}>
                                            {isRTL ? 'ملاحظات الوكيل' : 'Agent Notes'}
                                        </Typography>
                                        <Typography variant="body2">{d.chat_agent_notes}</Typography>
                                    </Alert>
                                )}

                                {/* Chat Transcript */}
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                                    {isRTL ? 'سجل المحادثة' : 'Chat Transcript'}
                                </Typography>
                                <Box sx={{
                                    maxHeight: 500,
                                    overflowY: 'auto',
                                    bgcolor: 'action.hover',
                                    borderRadius: 2,
                                    p: 2,
                                }}>
                                    {(() => {
                                        // Pre-group lines into messages: continuation lines merge into previous message
                                        const lines = d.chat_transcript.split('\n');
                                        const messages = [];
                                        for (let li = 0; li < lines.length; li++) {
                                            const line = lines[li];
                                            if (!line.trim()) continue;
                                            const match = line.match(/^\[([^\]]+)\]\s+(.+?):\s(.*)$/);
                                            if (match) {
                                                messages.push({
                                                    timestamp: match[1],
                                                    senderName: match[2],
                                                    messageText: match[3],
                                                    lineIndex: li,
                                                });
                                            } else if (messages.length > 0) {
                                                // Continuation line — append to previous message
                                                messages[messages.length - 1].messageText += '\n' + line;
                                            }
                                        }
                                        return messages.map((msg, i) => {
                                            const { timestamp, senderName, messageText, lineIndex } = msg;
                                            const isAgent = senderName !== 'Visitor';
                                            const isArabicText = /[\u0600-\u06FF]/.test(messageText);




                                            return (
                                                <Box key={i} sx={{
                                                    display: 'flex',
                                                    justifyContent: isAgent ? 'flex-end' : 'flex-start',
                                                    mb: 1,
                                                }}>
                                                    <Box sx={{
                                                        maxWidth: '80%',
                                                        px: 1.5,
                                                        py: 0.75,
                                                        borderRadius: 2,
                                                        bgcolor: isAgent ? 'primary.main' : 'background.paper',
                                                        color: isAgent ? '#fff' : 'text.primary',
                                                        border: isAgent ? 'none' : '1px solid',
                                                        borderColor: 'divider',
                                                        boxShadow: 1,
                                                        ...(isArabicText && { direction: 'rtl', textAlign: 'right' }),
                                                    }}>
                                                        <Typography variant="caption" sx={{
                                                            fontWeight: 700,
                                                            display: 'block',
                                                            mb: 0.25,
                                                            opacity: 0.85,
                                                            color: isAgent ? 'rgba(255,255,255,0.85)' : 'text.secondary',
                                                        }}>
                                                            {senderName} {'·'} {timestamp}
                                                        </Typography>
                                                        {(() => {
                                                            // Build a lookup of chat attachments from API data (keyed by index)
                                                            const chatAttachments = d.chat_attachments || [];

                                                            // Check if this is a voice message line (🎤)
                                                            const isVoiceLine = messageText.includes('🎤');
                                                            // Check if this is an attachment line (📎)
                                                            const isFileLine = messageText.includes('📎');

                                                            if (isVoiceLine || isFileLine) {
                                                                let fileUrl = null;
                                                                let fileName = null;
                                                                let msgType = isVoiceLine ? 'voice' : 'file';

                                                                // Try 1: Extract from markdown-link format [label](url)
                                                                const mdMatch = messageText.match(/\[([^\]]*)\]\(([^)]+)\)/);
                                                                if (mdMatch) {
                                                                    fileName = mdMatch[1];
                                                                    const rawUrl = mdMatch[2];
                                                                    const cleanUrl = rawUrl.replace(/^\//, '');
                                                                    fileUrl = rawUrl.startsWith('http') ? rawUrl : `${window.location.origin}/api/${cleanUrl}`;
                                                                }

                                                                // Try 2: Match against chat_attachments array from API
                                                                if (!fileUrl && chatAttachments.length > 0) {
                                                                    // Count how many attachment lines we've seen before this one
                                                                    const allLines = d.chat_transcript.split('\n');
                                                                    let attachIdx = 0;
                                                                    for (let li = 0; li < lineIndex; li++) {
                                                                        const l = allLines[li] || '';
                                                                        if (l.includes('🎤') || l.includes('📎')) attachIdx++;
                                                                    }
                                                                    const att = chatAttachments[attachIdx];
                                                                    if (att && att.file_url) {
                                                                        const cleanUrl = att.file_url.replace(/^\//, '');
                                                                        fileUrl = att.file_url.startsWith('http') ? att.file_url : `${window.location.origin}/api/${cleanUrl}`;
                                                                        fileName = att.file_name || fileName;
                                                                        msgType = att.message_type || msgType;
                                                                    } else if (att && att.file_name) {
                                                                        fileUrl = `${window.location.origin}/api/uploads/chat/${att.file_name}`;
                                                                        fileName = att.file_name;
                                                                        msgType = att.message_type || msgType;
                                                                    }
                                                                }

                                                                // Try 3: Extract filename from old format "📎 FILENAME.ext"
                                                                if (!fileUrl && isFileLine) {
                                                                    const fnMatch = messageText.match(/📎\s*(.+)/);
                                                                    if (fnMatch) {
                                                                        const extractedName = fnMatch[1].trim();
                                                                        if (/\.\w{2,5}$/.test(extractedName)) {
                                                                            fileUrl = `${window.location.origin}/api/uploads/chat/${extractedName}`;
                                                                            fileName = extractedName;
                                                                        }
                                                                    }
                                                                }

                                                                // Render media if we have a URL
                                                                if (fileUrl) {
                                                                    const isAudio = msgType === 'voice' || /\.(ogg|mp3|wav|webm|m4a|aac)$/i.test(fileUrl);
                                                                    const isImgFile = msgType === 'image' || /\.(jpg|jpeg|png|gif|webp)$/i.test(fileUrl);

                                                                    if (isImgFile) {
                                                                        return (
                                                                            <Box
                                                                                component="img"
                                                                                src={fileUrl}
                                                                                alt={fileName || 'attachment'}
                                                                                sx={{ maxWidth: '100%', maxHeight: 200, borderRadius: 1, cursor: 'pointer', mt: 0.5 }}
                                                                                onClick={() => window.open(fileUrl, '_blank')}
                                                                            />
                                                                        );
                                                                    } else if (isAudio) {
                                                                        return (
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                                                <VolumeUp sx={{ fontSize: 18, color: isAgent ? 'rgba(255,255,255,0.8)' : '#1a3a6b' }} />
                                                                                <Box
                                                                                    component="audio"
                                                                                    controls
                                                                                    preload="metadata"
                                                                                    src={fileUrl}
                                                                                    sx={{
                                                                                        height: 36,
                                                                                        maxWidth: 220,
                                                                                        borderRadius: 2,
                                                                                        '&::-webkit-media-controls-panel': { bgcolor: 'transparent' },
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                        );
                                                                    } else {
                                                                        return (
                                                                            <Button
                                                                                size="small"
                                                                                variant="text"
                                                                                startIcon={<Download />}
                                                                                onClick={() => window.open(fileUrl, '_blank')}
                                                                                sx={{ color: isAgent ? '#fff' : 'primary.main', textTransform: 'none' }}
                                                                            >
                                                                                {fileName || (isRTL ? 'مرفق' : 'Attachment')}
                                                                            </Button>
                                                                        );
                                                                    }
                                                                }
                                                            }

                                                            // Default: plain text
                                                            return (
                                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', ...(isArabicText && { direction: 'rtl', textAlign: 'right', unicodeBidi: 'embed' }) }}>
                                                                    {messageText}
                                                                </Typography>
                                                            );
                                                        })()}
                                                    </Box>
                                                </Box>
                                            )
                                        });
                                    })()}
                                </Box>

                                {/* Rating */}
                                {d.chat_rating > 0 && (
                                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {isRTL ? 'تقييم الزائر' : 'Visitor Rating'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                            {Array.from({ length: d.chat_rating }, (_, i) => (
                                                <Star key={i} sx={{ fontSize: 28, color: '#ed8936' }} />
                                            ))}
                                            {Array.from({ length: 5 - d.chat_rating }, (_, i) => (
                                                <Star key={`e${i}`} sx={{ fontSize: 28, color: '#e2e8f0' }} />
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        )
                    })() : (() => {
                        // Parse form fields
                        let formFields = submission.form_fields;
                        if (typeof formFields === 'string') {
                            try {
                                formFields = JSON.parse(formFields);
                            } catch (e) {
                                formFields = [];
                            }
                        }
                        if (!Array.isArray(formFields)) formFields = [];

                        const submissionData = submission.data || {};

                        // Helper: Get the base URL for file access
                        const getFileUrl = (filePath) => {
                            if (!filePath || typeof filePath !== 'string') return '';
                            const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
                            let normalizedPath = filePath;
                            if (filePath.startsWith('public/')) {
                                normalizedPath = filePath.substring(7);
                            }
                            return `${baseUrl}/public/${normalizedPath}`;
                        };

                        // Helper: Check if a string is a file path
                        const isFilePath = (val) => {
                            return typeof val === 'string' &&
                                (val.includes('uploads/') || val.includes('public/uploads'));
                        };

                        // Helper: Check if file is an image
                        const isImage = (filePath) => {
                            if (!filePath || typeof filePath !== 'string') return false;
                            const ext = filePath.split('.').pop()?.toLowerCase() || '';
                            return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
                        };

                        // Helper: Render file buttons (inline)
                        const renderFileActions = (filePath) => {
                            if (!filePath || typeof filePath !== 'string') return null;
                            const url = getFileUrl(filePath);
                            const filename = filePath.split('/').pop();
                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Tooltip title={language === 'ar' ? 'عرض' : 'View'}>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => {
                                                if (isImage(filePath)) {
                                                    setFilePreview({ url, filename, isImage: true });
                                                } else {
                                                    window.open(url, '_blank');
                                                }
                                            }}
                                        >
                                            <Visibility fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={language === 'ar' ? 'تحميل' : 'Download'}>
                                        <IconButton
                                            size="small"
                                            color="success"
                                            onClick={() => downloadFile(url, filename)}
                                        >
                                            <Download fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Typography variant="caption" color="text.secondary">
                                        {filename}
                                    </Typography>
                                </Box>
                            );
                        };

                        // Helper: Render a single value (handles files, objects, primitives)
                        const renderValue = (val, parentFieldId = null, subFieldDefs = null) => {
                            // Empty or null
                            if (val === null || val === undefined || val === '') {
                                return <Typography variant="body2" color="text.secondary">-</Typography>;
                            }

                            // File path
                            if (isFilePath(val)) {
                                return renderFileActions(val);
                            }

                            // Array (repeater data) - use subFieldDefs for ordering
                            if (Array.isArray(val)) {
                                if (val.length === 0) {
                                    return <Typography variant="body2" color="text.secondary">{language === 'ar' ? 'لا توجد بيانات' : 'No data'}</Typography>;
                                }
                                return (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                                        {val.map((item, idx) => (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    p: 1.5,
                                                    bgcolor: 'background.paper',
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                <Typography variant="caption" color="primary.main" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
                                                    #{idx + 1}
                                                </Typography>
                                                {typeof item === 'object' && item !== null ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        {/* Render subfields in defined order if available */}
                                                        {subFieldDefs && Array.isArray(subFieldDefs) ? (
                                                            subFieldDefs.map((subField) => {
                                                                const subValue = item[subField.id];
                                                                const subLabel = language === 'ar' && subField.label_ar
                                                                    ? subField.label_ar
                                                                    : (subField.label_en || subField.label || subField.id);
                                                                return (
                                                                    <Box key={subField.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, fontWeight: 500 }}>
                                                                            {subLabel}:
                                                                        </Typography>
                                                                        <Box sx={{ flex: 1 }}>
                                                                            {renderValue(subValue)}
                                                                        </Box>
                                                                    </Box>
                                                                );
                                                            })
                                                        ) : (
                                                            /* Fallback: render in object key order */
                                                            Object.entries(item).map(([k, v]) => (
                                                                <Box key={k} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, fontWeight: 500 }}>
                                                                        {getFieldLabel(k, parentFieldId)}:
                                                                    </Typography>
                                                                    <Box sx={{ flex: 1 }}>
                                                                        {renderValue(v)}
                                                                    </Box>
                                                                </Box>
                                                            ))
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2">{String(item)}</Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                );
                            }

                            // Object (nested data)
                            if (typeof val === 'object') {
                                // Check if it's a file object with name/path
                                if (val.path && isFilePath(val.path)) {
                                    return renderFileActions(val.path);
                                }
                                if (val.name && isFilePath(val.name)) {
                                    return renderFileActions(val.name);
                                }
                                // Empty object
                                if (Object.keys(val).length === 0) {
                                    return <Typography variant="body2" color="text.secondary">{language === 'ar' ? 'لا توجد بيانات' : 'No data'}</Typography>;
                                }
                                return (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {Object.entries(val).map(([k, v]) => (
                                            <Box key={k} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                                    {k}:
                                                </Typography>
                                                <Box sx={{ flex: 1 }}>
                                                    {renderValue(v)}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                );
                            }

                            // Primitive (string, number, boolean)
                            // Format time values (HH:MM) with AM/PM
                            const strVal = String(val);
                            const timeMatch = strVal.match(/^(\d{1,2}):(\d{2})$/);
                            if (timeMatch) {
                                let h = parseInt(timeMatch[1], 10);
                                const m = timeMatch[2];
                                const ampm = h >= 12 ? 'PM' : 'AM';
                                const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                                return <Typography variant="body2">{`${h12}:${m} ${ampm}`}</Typography>;
                            }
                            return <Typography variant="body2">{strVal}</Typography>;
                        };

                        // Render fields in form-defined order wrapped in Grid container
                        return (
                            <Grid container spacing={2}>
                                {formFields.map((field) => {
                                    const key = field.id;
                                    const value = submissionData[key];
                                    const label = language === 'ar' && field.label_ar
                                        ? field.label_ar
                                        : (field.label_en || field.label || getFieldLabel(key));

                                    // Determine grid width based on field.width or field type
                                    const isRepeater = field.type === 'repeater';
                                    const isComplexField = isRepeater || Array.isArray(value) || (typeof value === 'object' && value !== null && !isFilePath(value));

                                    // Use field width if defined, otherwise default based on complexity
                                    let gridWidth = 6; // default half width
                                    if (field.width === '100' || isComplexField) {
                                        gridWidth = 12;
                                    } else if (field.width === '50') {
                                        gridWidth = 6;
                                    } else if (field.width === '33') {
                                        gridWidth = 4;
                                    } else if (field.width === '25') {
                                        gridWidth = 3;
                                    }

                                    return (
                                        <Grid item xs={12} md={gridWidth} key={key}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    bgcolor: 'action.hover',
                                                    borderRadius: 2,
                                                    minHeight: 70,
                                                }}
                                            >
                                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                                    {label}
                                                </Typography>
                                                <Box sx={{ mt: 0.5 }}>
                                                    {renderValue(value, key, field.fields)}
                                                </Box>
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        );
                    })()}

                    {/* Attached Documents Section */}
                    {submission.documents && submission.documents.length > 0 && (
                        <>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AttachFile color="primary" />
                                {isRTL ? 'المستندات المرفقة' : 'Attached Documents'}
                            </Typography>
                            <Grid container spacing={2}>
                                {submission.documents.map((doc, idx) => {
                                    // Build correct URL:
                                    // VITE_API_URL = https://forms.moj.gov.sy/api
                                    // file_path = uploads/submissions/20/documents/47/filename.png
                                    // Files are served from /api/public/uploads/... on the server
                                    const apiBase = import.meta.env.VITE_API_URL || '';
                                    const fileUrl = `${apiBase}/public/${doc.file_path}`;
                                    const ext = doc.file_path.split('.').pop()?.toLowerCase() || '';
                                    const isImg = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
                                    const isPdf = ext === 'pdf';

                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={idx}>
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    bgcolor: 'background.default',
                                                    '&:hover': { borderColor: 'primary.main' }
                                                }}
                                            >
                                                <Box sx={{
                                                    width: 40, height: 40, borderRadius: 1,
                                                    bgcolor: 'primary.50', display: 'flex',
                                                    alignItems: 'center', justifyContent: 'center',
                                                    flexShrink: 0
                                                }}>
                                                    {isImg ? <Visibility color="primary" /> : <AttachFile color="primary" />}
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="subtitle2" noWrap title={doc.original_name}>
                                                        {doc.original_name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        {formatDate(doc.created_at, 'PPP', language)} • {language === 'ar' ? doc.user_name_ar || doc.user_name : doc.user_name_en || doc.user_name}
                                                    </Typography>
                                                </Box>
                                                {/* Eye icon: preview modal for images/PDFs, new tab for others */}
                                                <Tooltip title={isRTL ? 'عرض' : 'View'}>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => {
                                                            setImageError(false);
                                                            if (isImg) {
                                                                setFilePreview({ url: fileUrl, filename: doc.original_name, isImage: true });
                                                            } else if (isPdf) {
                                                                setFilePreview({ url: fileUrl, filename: doc.original_name, isImage: false });
                                                            } else {
                                                                window.open(fileUrl, '_blank');
                                                            }
                                                        }}
                                                    >
                                                        <Visibility fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={isRTL ? 'تحميل' : 'Download'}>
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        onClick={() => downloadFile(fileUrl, doc.original_name)}
                                                    >
                                                        <Download fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Paper>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </>
                    )}

                    {/* Department Approval Section - for end-user submissions, visible to admin, form_manager and departments */}
                    {isEndUserSubmission && (isDepartmentUser || isAdmin || isFormManager) && allApprovals.length > 0 && (
                        <>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {isRTL ? 'موافقات الأقسام' : 'Department Approvals'}
                            </Typography>

                            {/* Approval Timeline with Notes */}
                            <ApprovalTimeline approvals={allApprovals} isRTL={isRTL} showNotes={true} />

                            {/* My Approval Action Panel - only for department users with pending approval */}
                            {isDepartmentUser && myApproval && (
                                <DepartmentApprovalPanel
                                    approval={myApproval}
                                    submissionId={submission.id}
                                    onActionComplete={() => refetchApproval()}
                                />
                            )}

                            {/* QR Code for fully approved submissions */}
                            {allApprovals.length > 0 && allApprovals.every(a => a.status === 'approved') && submission.verification_code && (
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        mt: 3,
                                        textAlign: 'center',
                                        bgcolor: 'success.light',
                                        borderColor: 'success.main'
                                    }}
                                >
                                    <Typography variant="subtitle2" color="success.dark" gutterBottom>
                                        {isRTL ? 'تم الموافقة على الطلب - رمز التحقق' : 'Request Approved - Verification Code'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                        <Box
                                            component="img"
                                            src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(submission.verification_code)}`}
                                            alt="QR Code"
                                            sx={{ borderRadius: 1, bgcolor: 'white', p: 0.5 }}
                                        />
                                        <Box sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                                            <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                                                {submission.verification_code}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {isRTL ? 'امسح رمز QR للتحقق' : 'Scan QR code to verify'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            )}
                        </>
                    )}

                    {/* Staff Notes Section (Internal tracking for admins/managers) */}
                    {(isAdmin || isFormManager || isDashboardUser) && (
                        <Box sx={{ mt: 4 }}>
                            <Divider sx={{ mb: 3 }} />
                            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Shield color="primary" />
                                {t('submission.staffNotes')}
                            </Typography>
                            
                            <Box sx={{ mb: 3 }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    placeholder={t('submission.addStaffNote')}
                                    value={newStaffNote}
                                    onChange={(e) => setNewStaffNote(e.target.value)}
                                    disabled={isAddingStaffNote}
                                    InputProps={{ sx: { bgcolor: 'background.default' } }}
                                />
                                <Button
                                    variant="contained"
                                    startIcon={isAddingStaffNote ? <CircularProgress size={20} color="inherit" /> : <Comment />}
                                    onClick={handleAddStaffNote}
                                    disabled={!newStaffNote.trim() || isAddingStaffNote}
                                    sx={{ mt: 1 }}
                                >
                                    {isAddingStaffNote ? t('common.sending') : t('common.submit')}
                                </Button>
                            </Box>

                            {submission.notes?.length > 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {submission.notes.map((note) => (
                                        <Paper
                                            key={note.id}
                                            variant="outlined"
                                            sx={{
                                                p: 2,
                                                bgcolor: 'primary.50',
                                                borderLeft: '4px solid',
                                                borderLeftColor: 'primary.main',
                                                borderRight: isRTL ? '4px solid' : 'none',
                                                borderRightColor: isRTL ? 'primary.main' : 'transparent',
                                                position: 'relative'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, pr: isRTL ? 0 : 4, pl: isRTL ? 4 : 0 }}>
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight={600} color="primary.dark">
                                                        {isRTL ? note.user_name_ar || note.user_name_en : note.user_name_en || note.user_name_ar}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                        {formatDate(note.created_at, 'PPP p', language)} • {note.user_type}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{note.note}</Typography>
                                            {(isAdmin || user?.id === note.user_id) && (
                                                <IconButton 
                                                    size="small" 
                                                    color="error"
                                                    onClick={() => handleRequestDeleteNote(note.id)}
                                                    disabled={isDeletingStaffNote === note.id}
                                                    sx={{ position: 'absolute', top: 8, right: isRTL ? 'auto' : 8, left: isRTL ? 8 : 'auto' }}
                                                >
                                                    {isDeletingStaffNote === note.id ? <CircularProgress size={16} color="error" /> : <Close fontSize="small" />}
                                                </IconButton>
                                            )}
                                        </Paper>
                                    ))}
                                </Box>
                            ) : (
                                <Typography color="text.secondary" variant="body2">{isRTL ? 'لا توجد ملاحظات إدارة حتى الآن.' : 'No staff notes yet.'}</Typography>
                            )}
                        </Box>
                    )}
                </Paper>
            )}

            {/* Comments Tab - only for navigated submissions (not end-user submissions) */}
            {!isEndUserSubmission && activeTab === 1 && (
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        {t('submission.comments')}
                    </Typography>

                    {/* Add Comment (Department User) */}
                    {isDepartmentUser && (
                        <Box sx={{ mb: 3 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                placeholder={t('department.commentPlaceholder')}
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                            />
                            <Button
                                variant="contained"
                                startIcon={isCommenting ? <CircularProgress size={20} color="inherit" /> : <Send />}
                                onClick={handleAddComment}
                                disabled={!newComment.trim() || isCommenting}
                                sx={{ mt: 1 }}
                            >
                                {isCommenting ? t('common.sending') : t('department.comment')}
                            </Button>
                        </Box>
                    )}

                    {/* Comments List */}
                    {(comments || submission.comments)?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {(comments || submission.comments).map((comment) => (
                                <Box
                                    key={comment.id}
                                    sx={{
                                        p: 2,
                                        bgcolor: 'background.default',
                                        borderRadius: 2,
                                        borderLeft: '4px solid',
                                        borderColor: 'primary.main',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            {language === 'ar' ? comment.user_name_ar : comment.user_name_en}
                                        </Typography>
                                        <Chip
                                            label={language === 'ar' ? comment.department_name_ar : comment.department_name_en}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </Box>
                                    <Typography variant="body2">{comment.comment}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        {formatDate(comment.created_at, 'PPP p', language)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Typography color="text.secondary">{t('department.noComments')}</Typography>
                    )}
                </Paper>
            )}

            {/* History Tab - index 2 for navigated, 1 for end-user submissions */}
            {activeTab === (isEndUserSubmission ? 1 : 2) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Email Responses Section */}
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Email color="primary" />
                            {language === 'ar' ? 'ردود البريد الإلكتروني' : 'Email Responses'}
                        </Typography>

                        {submission.email_responses?.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {submission.email_responses.map((email) => (
                                    <Box
                                        key={email.id}
                                        sx={{
                                            p: 2,
                                            bgcolor: 'success.50',
                                            borderRadius: 2,
                                            borderLeft: '4px solid',
                                            borderColor: 'success.main',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                            <Box>
                                                <Typography fontWeight={600} color="success.dark">
                                                    {language === 'ar'
                                                        ? (email.subject_ar || email.subject_en || email.subject || '-')
                                                        : (email.subject_en || email.subject || '-')
                                                    }
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                                    <Person fontSize="small" color="action" />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {language === 'ar' ? 'الراد:' : 'Responded by:'} <strong>{language === 'ar' ? email.user_name_ar : email.user_name_en}</strong>
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" color="text.secondary">
                                                    {language === 'ar' ? 'إلى:' : 'To:'} {email.recipient_email}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDate(email.sent_at, 'PPP p', language)}
                                                </Typography>
                                            </Box>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<Visibility />}
                                                onClick={() => setViewEmailData(email)}
                                            >
                                                {language === 'ar' ? 'عرض' : 'View'}
                                            </Button>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography color="text.secondary">
                                {language === 'ar' ? 'لا توجد ردود بريد إلكتروني' : 'No email responses'}
                            </Typography>
                        )}
                    </Paper>

                    {/* Navigation History Section */}
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business color="primary" />
                            {t('submission.navigationHistory')}
                        </Typography>

                        {submission.navigations?.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {submission.navigations.map((nav) => (
                                    <Box
                                        key={nav.id}
                                        sx={{
                                            p: 2,
                                            bgcolor: 'action.hover',
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Business color="primary" />
                                            <Typography fontWeight={600}>
                                                {language === 'ar' ? nav.department_name_ar : nav.department_name_en}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            {t('department.navigatedBy')}: {language === 'ar' ? nav.navigated_by_name_ar : nav.navigated_by_name_en}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(nav.created_at, 'PPP p', language)}
                                        </Typography>
                                        {nav.notes && (
                                            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                                {nav.notes}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography color="text.secondary">
                                {language === 'ar' ? 'لا يوجد سجل تنقل' : 'No navigation history'}
                            </Typography>
                        )}
                    </Paper>

                    {/* Action History Section (Archive/Restore) */}
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            📋
                            {language === 'ar' ? 'سجل الإجراءات' : 'Action History'}
                        </Typography>

                        {submission.action_history?.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {submission.action_history.map((action) => (
                                    <Box
                                        key={action.id}
                                        sx={{
                                            p: 2,
                                            bgcolor: action.action === 'archived' ? 'warning.50' : action.action === 'document_uploaded' ? 'info.50' : 'success.50',
                                            borderRadius: 2,
                                            borderLeft: '4px solid',
                                            borderColor: action.action === 'archived' ? 'warning.main' : action.action === 'document_uploaded' ? 'info.main' : 'success.main',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip
                                                label={action.action === 'archived'
                                                    ? (language === 'ar' ? 'مؤرشف' : 'Archived')
                                                    : action.action === 'document_uploaded'
                                                    ? (language === 'ar' ? 'مستند مرفق' : 'Doc Uploaded')
                                                    : (language === 'ar' ? 'مستعاد' : 'Restored')}
                                                size="small"
                                                color={action.action === 'archived' ? 'warning' : action.action === 'document_uploaded' ? 'info' : 'success'}
                                            />
                                            <Typography fontWeight={600}>
                                                {language === 'ar' ? action.user_name_ar : action.user_name_en}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            {action.user_email} ({action.user_type})
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(action.created_at, 'PPP p', language)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography color="text.secondary">
                                {language === 'ar' ? 'لا يوجد سجل إجراءات' : 'No action history'}
                            </Typography>
                        )}
                    </Paper>

                    {/* Status Change History Section */}
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SwapHoriz color="primary" />
                            {t('statusHistory.title')}
                        </Typography>

                        {statusHistoryLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : statusHistory.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {statusHistory.map((log) => (
                                    <Box
                                        key={log.id}
                                        sx={{
                                            p: 2,
                                            bgcolor: log.action === 'status_change' ? 'info.50' : (log.action === 'created' ? 'success.50' : 'action.hover'),
                                            borderRadius: 2,
                                            borderLeft: '4px solid',
                                            borderColor: log.action === 'status_change' ? 'info.main' : (log.action === 'created' ? 'success.main' : 'grey.400'),
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Chip
                                                label={log.action === 'status_change'
                                                    ? (language === 'ar' ? 'تغيير حالة' : 'Status Change')
                                                    : log.action === 'created'
                                                        ? t('statusHistory.created')
                                                        : log.action}
                                                size="small"
                                                color={log.action === 'status_change' ? 'info' : (log.action === 'created' ? 'success' : 'default')}
                                            />
                                            {log.old_status && log.new_status && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Chip
                                                        label={t(`status.${log.old_status}`)}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.7rem' }}
                                                    />
                                                    <Typography variant="body2" color="text.secondary">→</Typography>
                                                    <Chip
                                                        label={t(`status.${log.new_status}`)}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.7rem' }}
                                                    />
                                                </Box>
                                            )}
                                        </Box>
                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('statusHistory.changedBy')}: <strong>{language === 'ar' ? log.user_name_ar : log.user_name_en}</strong>
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {log.user_email} ({log.user_type}) — {formatDate(log.created_at, 'PPP p', language)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Typography color="text.secondary">
                                {t('statusHistory.noHistory')}
                            </Typography>
                        )}
                    </Paper>
                </Box>
            )}

            {/* Metadata Tab - index 3 for navigated, 2 for end-user submissions */}
            {activeTab === (isEndUserSubmission ? 2 : 3) && (
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        {t('submission.metadata')}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                                {t('submission.ipAddress')}
                            </Typography>
                            <Typography>{submission.ip_address || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">
                                {t('submission.submittedAt')}
                            </Typography>
                            <Typography>
                                {formatDate(submission.created_at, 'PPP p', language)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                                {t('submission.userAgent')}
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                {submission.user_agent || '-'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Modals */}
            <Dialog open={uploadModalOpen} onClose={() => setUploadModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudUpload color="primary" />
                    {isRTL ? 'إرفاق مستند' : 'Upload Document'}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            {isRTL ? 'اختر ملفاً لرفعه وإرفاقه بهذا الطلب.' : 'Select a file to upload and attach to this submission.'}
                        </Typography>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<AttachFile />}
                            fullWidth
                            sx={{ height: 100, borderStyle: 'dashed' }}
                        >
                            {uploadFile ? uploadFile.name : (isRTL ? 'اختر ملفاً' : 'Choose File')}
                            <input
                                type="file"
                                hidden
                                onChange={(e) => setUploadFile(e.target.files[0])}
                            />
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadModalOpen(false)} disabled={isUploading}>
                        {isRTL ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button 
                        onClick={handleUploadDocument} 
                        variant="contained" 
                        disabled={!uploadFile || isUploading}
                        startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
                    >
                        {isRTL ? 'رفع' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            <EmailModal
                open={emailModalOpen}
                onClose={() => setEmailModalOpen(false)}
                submission={submission}
            />
            <NavigateModal
                open={navigateModalOpen}
                onClose={() => setNavigateModalOpen(false)}
                submissionId={submission.id}
                formId={submission.form_id}
            />

            {/* View Email Response Dialog */}
            <Dialog
                open={!!viewEmailData}
                onClose={() => setViewEmailData(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email color="primary" />
                    {language === 'ar' ? 'تفاصيل الرد' : 'Response Details'}
                </DialogTitle>
                <DialogContent dividers>
                    {viewEmailData && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {language === 'ar' ? 'إلى' : 'To'}
                                    </Typography>
                                    <Typography>{viewEmailData.recipient_email}</Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {language === 'ar' ? 'الراد' : 'Responded By'}
                                    </Typography>
                                    <Typography fontWeight={600}>
                                        {language === 'ar' ? viewEmailData.user_name_ar : viewEmailData.user_name_en}
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {language === 'ar' ? 'التاريخ' : 'Date'}
                                    </Typography>
                                    <Typography>{formatDate(viewEmailData.sent_at, 'PPP p', language)}</Typography>
                                </Box>
                            </Box>

                            <Divider />

                            {/* Subject - Both Languages */}
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Subject / الموضوع
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                                    <Box sx={{ flex: 1, minWidth: 200 }}>
                                        <Typography variant="caption" color="primary.main" fontWeight={600}>
                                            English
                                        </Typography>
                                        <Typography variant="h6">
                                            {viewEmailData.subject_en || viewEmailData.subject || '-'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 200, textAlign: 'right', direction: 'rtl' }}>
                                        <Typography variant="caption" color="primary.main" fontWeight={600}>
                                            العربية
                                        </Typography>
                                        <Typography variant="h6">
                                            {viewEmailData.subject_ar || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Message - Both Languages */}
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Message / الرسالة
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            flex: 1,
                                            minWidth: 200,
                                            p: 2,
                                            bgcolor: 'background.default',
                                            whiteSpace: 'pre-wrap'
                                        }}
                                    >
                                        <Typography variant="caption" color="primary.main" fontWeight={600} display="block" mb={1}>
                                            English
                                        </Typography>
                                        <Typography>
                                            {viewEmailData.message_en || viewEmailData.message || '-'}
                                        </Typography>
                                    </Paper>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            flex: 1,
                                            minWidth: 200,
                                            p: 2,
                                            bgcolor: 'background.default',
                                            whiteSpace: 'pre-wrap',
                                            textAlign: 'right',
                                            direction: 'rtl'
                                        }}
                                    >
                                        <Typography variant="caption" color="primary.main" fontWeight={600} display="block" mb={1}>
                                            العربية
                                        </Typography>
                                        <Typography>
                                            {viewEmailData.message_ar || '-'}
                                        </Typography>
                                    </Paper>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewEmailData(null)}>
                        {language === 'ar' ? 'إغلاق' : 'Close'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* File Preview Modal */}
            <Dialog
                open={!!filePreview}
                onClose={() => { setFilePreview(null); setImageError(false); }}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: 'background.paper',
                        maxHeight: '90vh',
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'primary.main',
                    color: 'white'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Visibility />
                        {filePreview?.filename || (language === 'ar' ? 'معاينة الملف' : 'File Preview')}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                            onClick={() => downloadFile(filePreview?.url, filePreview?.filename)}
                            disabled={downloadingFile}
                            sx={{ color: 'white' }}
                            title={language === 'ar' ? 'تحميل' : 'Download'}
                        >
                            <Download />
                        </IconButton>
                        <IconButton
                            onClick={() => setFilePreview(null)}
                            sx={{ color: 'white' }}
                        >
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{
                    p: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: '#e8e8e8',
                    minHeight: '400px'
                }}>
                    {filePreview?.isImage && !imageError ? (
                        <Box
                            component="img"
                            src={filePreview?.url}
                            alt={filePreview?.filename}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '80vh',
                                objectFit: 'contain',
                            }}
                            onError={() => setImageError(true)}
                        />
                    ) : imageError ? (
                        <Box sx={{
                            textAlign: 'center',
                            color: 'text.primary',
                            p: 4
                        }}>
                            <Typography variant="h4" sx={{ mb: 2 }}>⚠️</Typography>
                            <Typography variant="body1">
                                {language === 'ar' ? 'تعذر تحميل الصورة' : 'Failed to load image'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                                {filePreview?.url}
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<Download />}
                                onClick={() => downloadFile(filePreview?.url, filePreview?.filename)}
                                disabled={downloadingFile}
                                sx={{ mt: 2 }}
                            >
                                {language === 'ar' ? 'تحميل الملف' : 'Download File'}
                            </Button>
                        </Box>
                    ) : filePreview?.url?.toLowerCase().endsWith('.pdf') ? (
                        <Box sx={{ width: '100%', height: '80vh' }}>
                            <iframe
                                src={filePreview?.url}
                                title={filePreview?.filename}
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                            />
                        </Box>
                    ) : (
                        <Box sx={{
                            textAlign: 'center',
                            color: 'text.primary',
                            p: 4
                        }}>
                            <Typography variant="h6">
                                {language === 'ar' ? 'معاينة غير متاحة' : 'Preview not available'}
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<Download />}
                                onClick={() => downloadFile(filePreview?.url, filePreview?.filename)}
                                disabled={downloadingFile}
                                sx={{ mt: 2 }}
                            >
                                {language === 'ar' ? 'تحميل الملف' : 'Download File'}
                            </Button>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* PDF Export Language Selection Dialog */}
            <Dialog
                open={pdfExportDialogOpen}
                onClose={() => setPdfExportDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ pb: 1 }}>
                    {language === 'ar' ? 'اختر لغة التصدير' : 'Select Export Language'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {language === 'ar'
                            ? 'اختر اللغة التي تريد عرض أسماء الحقول بها في ملف PDF'
                            : 'Choose the language for field labels in the PDF export'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            fullWidth
                            onClick={() => {
                                console.log('Export settings:', submission.form_settings);
                                if (submission.data?.chat_transcript) {
                                    exportChatPDF(submission, 'ar', themeSettings);
                                } else {
                                    exportSubmissionPDF(
                                        submission,
                                        submission.form_fields,
                                        submission.form_settings,
                                        'ar',
                                        submission.email_responses || [],
                                        themeSettings
                                    );
                                }
                                setPdfExportDialogOpen(false);
                            }}
                            sx={{ py: 1.5 }}
                        >
                            🇸🇦 العربية (Arabic)
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            fullWidth
                            onClick={() => {
                                console.log('Export settings:', submission.form_settings);
                                if (submission.data?.chat_transcript) {
                                    exportChatPDF(submission, 'en', themeSettings);
                                } else {
                                    exportSubmissionPDF(
                                        submission,
                                        submission.form_fields,
                                        submission.form_settings,
                                        'en',
                                        submission.email_responses || [],
                                        themeSettings
                                    );
                                }
                                setPdfExportDialogOpen(false);
                            }}
                            sx={{ py: 1.5 }}
                        >
                            🇬🇧 English
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPdfExportDialogOpen(false)}>
                        {language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Leave Confirmation Dialog for Incomplete Submissions */}
            <Dialog
                open={leaveDialogOpen}
                onClose={handleLeaveCancel}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                    }
                }}
            >
                <Box sx={{
                    background: 'linear-gradient(135deg, #ff9800 0%, #f44336 100%)',
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                }}>
                    <Warning sx={{ fontSize: 40, color: '#fff' }} />
                    <Box>
                        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                            {language === 'ar' ? 'تنبيه — الطلب غير مكتمل' : 'Warning — Submission Incomplete'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                            {language === 'ar'
                                ? `الطلب ${submission?.submission_id || ''} لم يتم الرد عليه بعد`
                                : `Submission ${submission?.submission_id || ''} has not been replied to yet`
                            }
                        </Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ pt: 3, pb: 1 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {language === 'ar'
                            ? 'هل أنت متأكد أنك تريد المغادرة دون الرد على هذا الطلب؟ الطلب لا يزال بحاجة إلى اتخاذ إجراء.'
                            : 'Are you sure you want to leave without replying to this submission? It still requires action.'
                        }
                    </Typography>
                    <Box sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: '#fff3e0',
                        border: '1px solid #ffe0b2',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}>
                        <Warning sx={{ color: '#e65100', fontSize: 20 }} />
                        <Typography variant="body2" color="text.secondary">
                            {language === 'ar'
                                ? 'يُنصح بالرد على الطلب أو تحويله إلى القسم المعني قبل المغادرة'
                                : 'It is recommended to reply or forward this to the relevant department before leaving'
                            }
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        onClick={handleLeaveCancel}
                        variant="contained"
                        color="primary"
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
                    >
                        {language === 'ar' ? 'البقاء والمتابعة' : 'Stay & Continue Working'}
                    </Button>
                    <Button
                        onClick={handleLeaveConfirm}
                        variant="outlined"
                        color="error"
                        sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
                    >
                        {language === 'ar' ? 'مغادرة بدون رد' : 'Leave Without Replying'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={confirmDeleteNote.open}
                title={isRTL ? 'تأكيد الحذف' : 'Confirm Delete'}
                message={isRTL ? 'هل أنت متأكد من حذف هذه الملاحظة؟' : 'Are you sure you want to delete this note?'}
                confirmText={isRTL ? 'حذف' : 'Delete'}
                cancelText={isRTL ? 'إلغاء' : 'Cancel'}
                onConfirm={() => handleDeleteStaffNote(confirmDeleteNote.noteId)}
                onCancel={() => setConfirmDeleteNote({ open: false, noteId: null })}
            />
        </Box>
    )
}

export default SubmissionDetail
